import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Plus, Filter, RefreshCw, Building, Info } from 'lucide-react';
import { Reservation, ReservationFilters, PaginationParams, Restaurant } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useRestaurant } from '../../contexts/RestaurantContext';
import apiService from '../../services/api';
import ReservationCard from './ReservationCard';
import RestaurantInfo from './RestaurantInfo';

const ReservationList: React.FC = () => {
  const { user } = useAuth();
  const { restaurant } = useRestaurant();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10,
  });

  const [filters, setFilters] = useState<ReservationFilters>({
    status: '',
    data_inicio: '',
    data_fim: '',
    restaurante_id: undefined, // Only used for admin_global users
  });

  const [showFilters, setShowFilters] = useState(false);
  const [dateFilterMode, setDateFilterMode] = useState<'single' | 'range'>('single');
  const [monthlyTotal, setMonthlyTotal] = useState<number>(0);
  const [todayTotal, setTodayTotal] = useState<number>(0);
  const [filteredTotal, setFilteredTotal] = useState<number>(0);

  const fetchRestaurants = async () => {
    try {
      const response = await apiService.getRestaurants({ limit: 100 });
      setRestaurants(response.data);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    }
  };

  const fetchMonthlyTotal = useCallback(async () => {
    try {
      // Calculate first and last day of current month
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      // Format dates as YYYY-MM-DD
      const dataInicio = firstDay.toISOString().split('T')[0];
      const dataFim = lastDay.toISOString().split('T')[0];

      // Build params for monthly count - only confirmed reservations
      const params: any = {
        data_inicio: dataInicio,
        data_fim: dataFim,
        status: 'confirmada', // Only count confirmed reservations
        limit: 1, // We only need the count, not the data
      };

      // For non-admin users, automatically add restaurant filter
      if (user?.tipo !== 'admin_global' && restaurant?.id) {
        params.restaurante_id = restaurant.id;
      }

      const response = await apiService.getReservations(params);
      
      // Get total from pagination - handle both camelCase and snake_case
      if (response.pagination) {
        // API returns camelCase (totalItems), but type expects snake_case (total_items)
        const total = (response.pagination as any).totalItems ?? response.pagination.total_items;
        if (total !== undefined) {
          setMonthlyTotal(total);
        } else {
          // Fallback: count the data if pagination is not available
          const data = Array.isArray(response.data) ? response.data : [];
          setMonthlyTotal(data.length);
        }
      } else {
        // Fallback: count the data if pagination is not available
        const data = Array.isArray(response.data) ? response.data : [];
        setMonthlyTotal(data.length);
      }
    } catch (error) {
      console.error('Failed to fetch monthly total:', error);
      setMonthlyTotal(0);
    }
  }, [user?.tipo, restaurant?.id]);

  const fetchTodayTotal = useCallback(async () => {
    try {
      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];

      // Build params for today's count - only confirmed reservations
      const params: any = {
        data_inicio: today,
        data_fim: today,
        status: 'confirmada', // Only count confirmed reservations
        limit: 1, // We only need the count from pagination
      };

      // For non-admin users, automatically add restaurant filter
      if (user?.tipo !== 'admin_global' && restaurant?.id) {
        params.restaurante_id = restaurant.id;
      }

      const response = await apiService.getReservations(params);
      
      // Get total from pagination - handle both camelCase and snake_case
      if (response.pagination) {
        // API returns camelCase (totalItems), but type expects snake_case (total_items)
        const total = (response.pagination as any).totalItems ?? response.pagination.total_items;
        if (total !== undefined) {
          // Note: This count includes all confirmed reservations for today
          // The displayed list filters out canceled and failed payments, so the count might be slightly higher
          // But since we're filtering by status='confirmada', canceled shouldn't be included anyway
          setTodayTotal(total);
        } else {
          // Fallback: count the data if pagination is not available
          const data = Array.isArray(response.data) ? response.data : [];
          const filteredData = data.filter(reservation => 
            reservation.status !== 'cancelada' && 
            reservation.payment_status !== 'failed'
          );
          setTodayTotal(filteredData.length);
        }
      } else {
        // Fallback: count the data if pagination is not available
        const data = Array.isArray(response.data) ? response.data : [];
        const filteredData = data.filter(reservation => 
          reservation.status !== 'cancelada' && 
          reservation.payment_status !== 'failed'
        );
        setTodayTotal(filteredData.length);
      }
    } catch (error) {
      console.error('Failed to fetch today total:', error);
      setTodayTotal(0);
    }
  }, [user?.tipo, restaurant?.id]);

  // Check if any filters are active (excluding default today filter)
  const hasActiveFilters = useCallback(() => {
    return filters.status !== '' || 
           filters.data_inicio !== '' || 
           filters.data_fim !== '' ||
           filters.restaurante_id !== undefined;
  }, [filters]);

  const fetchReservations = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const hasFilters = hasActiveFilters();
      
      // If no filters, show today's confirmed reservations (no pagination)
      if (!hasFilters) {
        const today = new Date().toISOString().split('T')[0];
        const params: any = {
          data_inicio: today,
          data_fim: today,
          status: 'confirmada', // Only show confirmed reservations for today
          limit: 100, // API max limit is 100
        };

        // For non-admin users, automatically add restaurant filter
        if (user?.tipo !== 'admin_global' && restaurant?.id) {
          params.restaurante_id = restaurant.id;
        }

        const response = await apiService.getReservations(params);
        
        // Filter out canceled and failed payments (matching TodayReservations logic)
        let filteredData = Array.isArray(response.data) ? response.data : [];
        filteredData = filteredData.filter(reservation => 
          reservation.status !== 'cancelada' && 
          reservation.payment_status !== 'failed' &&
          reservation.status === 'confirmada' // Double check status is confirmed
        );
        
        // Sort by time
        filteredData.sort((a, b) => 
          new Date(a.data_hora).getTime() - new Date(b.data_hora).getTime()
        );
        
        setReservations(filteredData);
        
        // Update today's total to match what's displayed
        setTodayTotal(filteredData.length);
        
        // No pagination for today's view
        setPagination({
          current_page: 1,
          total_pages: 1,
          total_items: filteredData.length,
          items_per_page: filteredData.length
        });
      } else {
        // Filters are active - fetch all reservations (no pagination)
        const params: any = {
          limit: 100, // API max limit
          // Include all filters that have values
          ...Object.fromEntries(
            Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
          ),
        };

        // For non-admin users, automatically add restaurant filter
        if (user?.tipo !== 'admin_global' && restaurant?.id) {
          params.restaurante_id = restaurant.id;
        }

        // First, get the count with limit=1
        const countParams = { ...params, limit: 1 };
        const countResponse = await apiService.getReservations(countParams);
        
        // Get total from pagination for the filtered count
        let totalCount = 0;
        if (countResponse.pagination) {
          const paginationAny = countResponse.pagination as any;
          totalCount = paginationAny.totalItems ?? countResponse.pagination.total_items ?? 0;
        }
        setFilteredTotal(totalCount);

        // Now fetch all reservations (up to 100)
        const response = await apiService.getReservations(params);
        
        // Smart filtering: exclude canceled and failed payments UNLESS user explicitly filtered by "cancelada"
        let filteredData = Array.isArray(response.data) ? response.data : [];
        
        // Only apply default filtering if user hasn't explicitly selected "cancelada" status
        if (filters.status !== 'cancelada') {
          filteredData = filteredData.filter(reservation => 
            reservation.status !== 'cancelada' && 
            reservation.payment_status !== 'failed'
          );
        }
        
        setReservations(filteredData);
        
        // No pagination when filters are active - show all results
        setPagination({
          current_page: 1,
          total_pages: 1,
          total_items: filteredData.length,
          items_per_page: filteredData.length
        });
      }
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
      setReservations([]);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.current_page, pagination.items_per_page, filters, user?.tipo, restaurant?.id, hasActiveFilters]);

  useEffect(() => {
    // For non-admin users, wait for restaurant context to load
    if (user?.tipo === 'admin_global' || restaurant?.id) {
      fetchReservations();
      fetchMonthlyTotal(); // Fetch monthly total when component loads
      fetchTodayTotal(); // Always fetch today's total (for the stats box)
    }
    
    // Only fetch restaurants list if user is admin_global
    if (user?.tipo === 'admin_global') {
      fetchRestaurants();
    }
  }, [fetchReservations, fetchMonthlyTotal, fetchTodayTotal, user?.tipo, restaurant?.id]);

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await apiService.updateReservationStatus(id, { status: status as any });
      await fetchReservations(); // Refresh the list
    } catch (error) {
      console.error('Failed to update reservation status:', error);
    }
  };

  const handleCancelReservation = async (id: number) => {
    try {
      await apiService.cancelReservation(id);
      await fetchReservations(); // Refresh the list
    } catch (error) {
      console.error('Failed to cancel reservation:', error);
    }
  };

  const handleRefundReservation = async (id: number) => {
    try {
      await fetchReservations(); // Refresh the list to show updated payment status
    } catch (error) {
      console.error('Failed to refresh reservations after refund:', error);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // If single day mode and data_inicio is changed, set both dates to the same value
    if (dateFilterMode === 'single' && name === 'data_inicio') {
      setFilters(prev => ({
        ...prev,
        data_inicio: value,
        data_fim: value, // Set both to the same date
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value,
      }));
    }
    setPagination(prev => ({ ...prev, current_page: 1 })); // Reset to first page
  };

  const handleDateFilterModeChange = (mode: 'single' | 'range') => {
    setDateFilterMode(mode);
    // Clear date filters when switching modes
    setFilters(prev => ({
      ...prev,
      data_inicio: '',
      data_fim: '',
    }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      data_inicio: '',
      data_fim: '',
      restaurante_id: undefined,
    });
    setDateFilterMode('single'); // Reset to default mode
    setFilteredTotal(0); // Reset filtered total
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const changePage = (page: number) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  const changeItemsPerPage = (itemsPerPage: number) => {
    setPagination(prev => ({ 
      ...prev, 
      items_per_page: itemsPerPage,
      current_page: 1 // Reset to first page when changing items per page
    }));
  };

  const getFilteredCount = () => {
    const activeFilters = Object.values(filters).filter(value => value !== '').length;
    return activeFilters;
  };

  // Generate page numbers with ellipsis for better UX
  const getPageNumbers = () => {
    const { current_page, total_pages } = pagination;
    const pages: (number | string)[] = [];
    const maxVisible = 7; // Maximum number of page buttons to show

    if (total_pages <= maxVisible) {
      // Show all pages if total is small
      return Array.from({ length: total_pages }, (_, i) => i + 1);
    }

    // Always show first page
    pages.push(1);

    if (current_page > 3) {
      pages.push('...');
    }

    // Show pages around current page
    const start = Math.max(2, current_page - 1);
    const end = Math.min(total_pages - 1, current_page + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current_page < total_pages - 2) {
      pages.push('...');
    }

    // Always show last page
    if (total_pages > 1) {
      pages.push(total_pages);
    }

    return pages;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold leading-7 text-secondary-900 sm:truncate sm:text-3xl">
            Reservas
          </h1>
          <p className="mt-1 text-sm text-secondary-500">
            Gerir todas as reservas do restaurante
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:ml-4 md:mt-0">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary flex items-center space-x-2 ${
              getFilteredCount() > 0 ? 'bg-primary-100 text-primary-700' : ''
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtros</span>
            {getFilteredCount() > 0 && (
              <span className="bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {getFilteredCount()}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              fetchReservations();
              fetchMonthlyTotal();
              fetchTodayTotal();
            }}
            disabled={isLoading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
          <Link to="/reservations/new" className="btn-primary flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Nova Reserva</span>
          </Link>
        </div>
      </div>

      {/* Restaurant Information */}
      <RestaurantInfo />

      {/* Info Banner - Hidden canceled reservations */}
      {filters.status !== 'cancelada' && !isLoading && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Reservas canceladas e com pagamento falhado estão ocultas por padrão. 
                Para ver reservas canceladas, selecione "Cancelada" no filtro de estado.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="card p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="status" className="label">
                Estado
              </label>
              <select
                id="status"
                name="status"
                className="input-field"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">Todos os estados</option>
                <option value="pendente">Pendente</option>
                <option value="confirmada">Confirmada</option>
                <option value="cancelada">Cancelada</option>
                <option value="concluida">Concluída</option>
              </select>
            </div>

            {/* Restaurant filter - only for admin_global users */}
            {user?.tipo === 'admin_global' && (
              <div>
                <label htmlFor="restaurante_id" className="label">
                  <Building className="inline h-4 w-4 mr-1" />
                  Restaurante
                </label>
                <select
                  id="restaurante_id"
                  name="restaurante_id"
                  className="input-field"
                  value={filters.restaurante_id || ''}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos os restaurantes</option>
                  {restaurants.map((restaurant) => (
                    <option key={restaurant.id} value={restaurant.id}>
                      {restaurant.nome}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Date Filter Mode Selection */}
            <div className="md:col-span-4">
              <label className="label mb-3">Filtro de Data</label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dateFilterMode"
                    value="single"
                    checked={dateFilterMode === 'single'}
                    onChange={() => handleDateFilterModeChange('single')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300"
                  />
                  <span className="text-sm text-secondary-700">Dia Específico</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dateFilterMode"
                    value="range"
                    checked={dateFilterMode === 'range'}
                    onChange={() => handleDateFilterModeChange('range')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300"
                  />
                  <span className="text-sm text-secondary-700">Intervalo de Datas</span>
                </label>
              </div>
            </div>

            {/* Single Day Filter */}
            {dateFilterMode === 'single' && (
              <div>
                <label htmlFor="data_inicio" className="label">
                  Data
                </label>
                <input
                  type="date"
                  id="data_inicio"
                  name="data_inicio"
                  className="input-field"
                  value={filters.data_inicio}
                  onChange={handleFilterChange}
                />
              </div>
            )}

            {/* Date Range Filters */}
            {dateFilterMode === 'range' && (
              <>
                <div>
                  <label htmlFor="data_inicio" className="label">
                    Data Início
                  </label>
                  <input
                    type="date"
                    id="data_inicio"
                    name="data_inicio"
                    className="input-field"
                    value={filters.data_inicio}
                    onChange={handleFilterChange}
                  />
                </div>

                <div>
                  <label htmlFor="data_fim" className="label">
                    Data Fim
                  </label>
                  <input
                    type="date"
                    id="data_fim"
                    name="data_fim"
                    className="input-field"
                    value={filters.data_fim}
                    onChange={handleFilterChange}
                  />
                </div>
              </>
            )}
          </div>

          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={clearFilters}
              className="btn-secondary"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="text-2xl font-semibold text-secondary-900">
            {(filters.data_inicio || filters.data_fim) ? filteredTotal : monthlyTotal}
          </div>
          <div className="text-sm text-secondary-600">
            {(filters.data_inicio || filters.data_fim) ? 'Total de Reservas' : 'Total de Reservas do Mês'}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-semibold text-green-600">
            {todayTotal}
          </div>
          <div className="text-sm text-secondary-600">Reservas de Hoje</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-semibold text-yellow-600">
            {Array.isArray(reservations) ? reservations.filter(r => r.status === 'pendente').length : 0}
          </div>
          <div className="text-sm text-secondary-600">Pendentes (página atual)</div>
        </div>
      </div>

      {/* Reservations List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-primary-600 mx-auto" />
            <p className="mt-2 text-sm text-secondary-600">A carregar reservas...</p>
          </div>
        </div>
      ) : reservations.length === 0 ? (
        <div className="card p-6 text-center">
          <Calendar className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 mb-2">
            Nenhuma reserva encontrada
          </h3>
          <p className="text-secondary-500 mb-4">
            {getFilteredCount() > 0 
              ? 'Tente ajustar os filtros de pesquisa.' 
              : 'Comece criando a primeira reserva.'}
          </p>
          {getFilteredCount() > 0 ? (
            <button onClick={clearFilters} className="btn-secondary">
              Limpar Filtros
            </button>
          ) : (
            <Link to="/reservations/new" className="btn-primary">
              Criar Primera Reserva
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {reservations.map((reservation) => (
            <ReservationCard
              key={reservation.id}
              reservation={reservation}
              onStatusChange={handleStatusChange}
              onCancel={handleCancelReservation}
              onRefund={handleRefundReservation}
            />
          ))}
        </div>
      )}

      {/* Pagination - Hidden when filters are active (we show all results) */}
      {false && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between border-t border-secondary-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => changePage(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="relative inline-flex items-center rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => changePage(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.total_pages}
              className="relative ml-3 inline-flex items-center rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div className="flex items-center space-x-4">
              <p className="text-sm text-secondary-700">
                Mostrando{' '}
                <span className="font-medium">
                  {(pagination.current_page - 1) * pagination.items_per_page + 1}
                </span>{' '}
                a{' '}
                <span className="font-medium">
                  {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)}
                </span>{' '}
                de{' '}
                <span className="font-medium">{pagination.total_items}</span> resultados
              </p>
              <div className="flex items-center space-x-2">
                <label htmlFor="items-per-page" className="text-sm text-secondary-700">
                  Por página:
                </label>
                <select
                  id="items-per-page"
                  value={pagination.items_per_page}
                  onChange={(e) => changeItemsPerPage(parseInt(e.target.value))}
                  className="rounded-md border-secondary-300 text-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => changePage(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-secondary-400 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                {getPageNumbers().map((page, index) => {
                  if (page === '...') {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-secondary-700 ring-1 ring-inset ring-secondary-300"
                      >
                        ...
                      </span>
                    );
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => changePage(page as number)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        page === pagination.current_page
                          ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                          : 'text-secondary-900 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => changePage(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.total_pages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-secondary-400 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próxima
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationList;
